import { useState, useRef, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import { useUpdateAnnouncement, useGetAnnouncementById, useGetAnnouncementDepartments } from 'services/api/announcements/announcementsHook'
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    ListItemText,
    MenuItem,
    Select,
    Skeleton,
    TextField,
    Typography,
    Popover,
} from '@mui/material'
import {
    AttachFile as AttachFileIcon,
    Close as CloseIcon,
    Check as CheckIcon,
    CloudUpload as CloudUploadIcon,
    FormatBold,
    FormatItalic,
    FormatListBulleted,
    FormatListNumbered,
    FormatUnderlined,
    InsertLink,
} from '@mui/icons-material'

const EditAnnouncementModal = ({ open, onClose, announcement }) => {
    const [title, setTitle] = useState('')
    const [targetType, setTargetType] = useState('all')
    const [selectedDeptIds, setSelectedDeptIds] = useState([])
    const [contentHtml, setContentHtml] = useState('')
    const [attachments, setAttachments] = useState([])
    const [removedAttachmentIds, setRemovedAttachmentIds] = useState([])
    const contentRef = useRef(null)
    const savedRangeRef = useRef(null)
    const [linkPopover, setLinkPopover] = useState({ open: false, anchorEl: null, url: '' })

    const { mutate: submitUpdate, isLoading } = useUpdateAnnouncement({
        onSuccess: () => handleClose(),
        onError: (error) => console.error('Failed to update announcement:', error),
    })

    // Fetch full announcement detail (the list endpoint omits `body` and `attachments`)
    const { data: detailData, isLoading: detailLoading } = useGetAnnouncementById(
        open ? announcement?.id : null
    )
    const fullAnnouncement = detailData?.data

    // Pre-populate fields once the full detail has loaded
    useEffect(() => {
        if (open && fullAnnouncement) {
            setTitle(fullAnnouncement.title || '')
            setTargetType(fullAnnouncement.target_type || 'all')
            setSelectedDeptIds(
                Array.isArray(fullAnnouncement.target_ids)
                    ? fullAnnouncement.target_ids.map(String)
                    : []
            )
            const html = fullAnnouncement.body || ''
            setContentHtml(html)
            setTimeout(() => {
                if (contentRef.current) contentRef.current.innerHTML = html
            }, 0)
            setAttachments(
                Array.isArray(fullAnnouncement.attachments)
                    ? fullAnnouncement.attachments.map((a) => ({
                        id: a.id,
                        name: a.file_name,
                        size: a.file_size,
                        sizeFormatted: formatFileSize(a.file_size || 0),
                        url: a.download_url || a.url,
                        _existing: true,
                    }))
                    : []
            )
            setRemovedAttachmentIds([])
        }
    }, [open, fullAnnouncement])

    const { data: deptData, isLoading: deptLoading } = useGetAnnouncementDepartments({ enabled: open })
    const deptSource = deptData?.data || deptData || []
    const deptList = Array.isArray(deptSource)
        ? (deptSource[0]?.departments
            ? deptSource.flatMap((layer) => layer.departments || [])
            : deptSource)
        : []

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    }

    const handleFileUpload = (acceptedFiles) => {
        if (!acceptedFiles || acceptedFiles.length === 0) return

        const MAX_FILES = 5
        const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB

        const currentCount = attachments.length
        const remainingSlots = MAX_FILES - currentCount

        if (remainingSlots <= 0) {
            console.error('You can only upload up to 5 files maximum.')
            return
        }

        const oversizedFiles = acceptedFiles.filter((file) => file.size > MAX_FILE_SIZE)
        const validFiles = acceptedFiles.filter((file) => file.size <= MAX_FILE_SIZE)

        if (oversizedFiles.length > 0) {
            const names = oversizedFiles.map((file) => file.name).join(', ')
            console.error(`Files exceed 5MB: ${names}`)
        }

        if (validFiles.length === 0) return

        const filesToAdd = validFiles.slice(0, remainingSlots)

        const newFiles = filesToAdd.map((file, index) => ({
            id: `${file.name}-${file.size}-${Date.now()}-${index}`,
            file,
            name: file.name,
            size: file.size,
            sizeFormatted: formatFileSize(file.size),
            type: file.type,
        }))

        setAttachments((prev) => [...prev, ...newFiles])
    }

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: handleFileUpload,
        disabled: attachments.length >= 5,
        multiple: true,
    })

    const handleDeleteFile = (fileId) => {
        setAttachments((prev) => prev.filter((f) => f.id !== fileId))
    }

    const handleViewFile = (file) => {
        if (file?.file instanceof File) {
            const url = URL.createObjectURL(file.file)
            window.open(url, '_blank')
            setTimeout(() => URL.revokeObjectURL(url), 100)
            return
        }

        if (file?.url) {
            window.open(file.url, '_blank', 'noopener,noreferrer')
        }
    }

    const handleClose = () => {
        setTitle('')
        setTargetType('all')
        setSelectedDeptIds([])
        setContentHtml('')
        setAttachments([])
        setRemovedAttachmentIds([])
        if (contentRef.current) contentRef.current.innerHTML = ''
        onClose()
    }

    const formatText = (command, anchorEl) => {
        if (command === 'createLink') {
            const selection = window.getSelection()
            if (selection && selection.rangeCount > 0) {
                savedRangeRef.current = selection.getRangeAt(0).cloneRange()
            }
            setLinkPopover({ open: true, anchorEl, url: '' })
            return
        }
        document.execCommand(command, false, null)
        contentRef.current?.focus()
    }

    const handleLinkConfirm = () => {
        const { url } = linkPopover
        if (url.trim() && savedRangeRef.current) {
            const selection = window.getSelection()
            selection.removeAllRanges()
            selection.addRange(savedRangeRef.current)
            document.execCommand('createLink', false, url.trim())
            savedRangeRef.current = null
        }
        setLinkPopover({ open: false, anchorEl: null, url: '' })
        contentRef.current?.focus()
    }

    const handleLinkClose = () => {
        savedRangeRef.current = null
        setLinkPopover({ open: false, anchorEl: null, url: '' })
        contentRef.current?.focus()
    }

    const isDisabled =
        isLoading ||
        !title.trim() ||
        !contentHtml.replace(/<[^>]*>/g, '').trim() ||
        (targetType === 'department' && selectedDeptIds.length === 0)

    const handleSubmit = () => {
        const newAttachments = attachments
            .filter((a) => a?.file instanceof File)
            .map((a) => a.file)

        const payload = {
            id: announcement?.id,
            title,
            body: contentHtml,
            target_type: targetType,
            target_ids: targetType === 'department' ? selectedDeptIds : null,
            attachments: newAttachments,
            remove_attachment_ids: removedAttachmentIds,
        }
        submitUpdate(payload)
    }

    const toolbarGroups = [
        [
            { cmd: 'bold', icon: <FormatBold sx={{ fontSize: 18 }} />, title: 'Bold' },
            { cmd: 'italic', icon: <FormatItalic sx={{ fontSize: 18 }} />, title: 'Italic' },
            { cmd: 'underline', icon: <FormatUnderlined sx={{ fontSize: 18 }} />, title: 'Underline' },
        ],
        [
            { cmd: 'insertUnorderedList', icon: <FormatListBulleted sx={{ fontSize: 18 }} />, title: 'Bullet List' },
            { cmd: 'insertOrderedList', icon: <FormatListNumbered sx={{ fontSize: 18 }} />, title: 'Numbered List' },
        ],
        [
            { cmd: 'createLink', icon: <InsertLink sx={{ fontSize: 18 }} />, title: 'Insert Link' },
        ],
    ]

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2.5 } }}
            >
                <DialogTitle sx={{ pb: 1.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <Box
                                sx={{
                                    width: 45,
                                    height: 45,
                                    borderRadius: '100%',
                                    // backgroundColor: '#FFF9E6',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 56 56">
                                    <path
                                        fill="#F3BF3A"
                                        d="M28 51.906c13.055 0 23.906-10.828 23.906-23.906c0-13.055-10.875-23.906-23.93-23.906C14.899 4.094 4.095 14.945 4.095 28c0 13.078 10.828 23.906 23.906 23.906m-.023-20.39c-1.243 0-1.922-.727-1.97-1.97L25.68 17.97c-.047-1.29.937-2.203 2.273-2.203c1.313 0 2.32.937 2.274 2.226l-.329 11.555c-.047 1.265-.75 1.969-1.921 1.969m0 8.625c-1.36 0-2.626-1.078-2.626-2.532s1.243-2.53 2.626-2.53c1.359 0 2.624 1.054 2.624 2.53c0 1.477-1.289 2.532-2.624 2.532"
                                    />
                                </svg>
                            </Box>
                            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: 'text.primary' }}>
                                Edit Announcement
                            </Typography>
                        </Box>
                        <IconButton onClick={handleClose} size="small" sx={{ color: 'text.primary' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers sx={{ py: 3, px: 3 }}>
                    {detailLoading && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                            <Skeleton variant="rounded" height={36} />
                            <Skeleton variant="rounded" height={120} />
                            <Skeleton variant="rounded" height={36} />
                        </Box>
                    )}
                    {/* Title */}
                    <Box mb={2.5} sx={{ display: detailLoading ? 'none' : 'block' }}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                            Title
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Enter Announcement title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '14px' },
                            }}
                        />
                    </Box>

                    {/* Content — rich text editor */}
                    <Box mb={2.5} sx={{ display: detailLoading ? 'none' : 'block' }}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                            Content
                        </Typography>
                        <Box
                            sx={{
                                border: '1px solid rgba(0,0,0,0.23)',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                '&:hover': { borderColor: 'rgba(0,0,0,0.87)' },
                                '&:focus-within': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
                            }}
                        >
                            {/* Toolbar */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '2px',
                                    px: '8px',
                                    py: '5px',
                                    borderBottom: '1px solid #E8E8E8',
                                    backgroundColor: '#FAFAFA',
                                }}
                            >
                                {toolbarGroups.map((group, gi) => (
                                    <Box key={gi} sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                        {gi > 0 && (
                                            <Box sx={{ width: '1px', height: '18px', backgroundColor: '#E0E0E0', mx: '4px' }} />
                                        )}
                                        {group.map(({ cmd, icon, title: btnTitle }) => (
                                            <IconButton
                                                key={cmd}
                                                size="small"
                                                title={btnTitle}
                                                onMouseDown={(e) => {
                                                    e.preventDefault()
                                                    formatText(cmd, e.currentTarget)
                                                }}
                                                sx={{ borderRadius: '4px', color: 'text.primary', p: '4px' }}
                                            >
                                                {icon}
                                            </IconButton>
                                        ))}
                                    </Box>
                                ))}
                            </Box>

                            {/* Editable area */}
                            <Box
                                ref={contentRef}
                                component="div"
                                contentEditable
                                suppressContentEditableWarning
                                onInput={(e) => setContentHtml(e.currentTarget.innerHTML)}
                                sx={{
                                    minHeight: '120px',
                                    p: '12px 14px',
                                    fontSize: '14px',
                                    color: 'text.primary',
                                    outline: 'none',
                                    lineHeight: 1.6,
                                    '&:empty::before': {
                                        content: '"Write something..."',
                                        color: 'rgba(0,0,0,0.4)',
                                        display: 'block',
                                        pointerEvents: 'none',
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Target Audience */}
                    <Box mb={2.5} sx={{ display: detailLoading ? 'none' : 'block' }}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                            Target Audience
                        </Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                multiple
                                value={targetType === 'all' ? ['all'] : selectedDeptIds}
                                onChange={(e) => {
                                    const val = e.target.value
                                    if (val[val.length - 1] === 'all') {
                                        setTargetType('all')
                                        setSelectedDeptIds([])
                                    } else {
                                        const deptIds = val.filter((v) => v !== 'all')
                                        setTargetType(deptIds.length > 0 ? 'department' : 'all')
                                        setSelectedDeptIds(deptIds)
                                    }
                                }}
                                displayEmpty
                                renderValue={() => {
                                    if (targetType === 'all') return 'All Departments'
                                    if (selectedDeptIds.length === 0) return (
                                        <Typography sx={{ color: 'rgba(0,0,0,0.4)', fontSize: '14px' }}>
                                            Select Target Audience
                                        </Typography>
                                    )
                                    return selectedDeptIds
                                        .map((id) => deptList.find((d) => String(d.id) === String(id))?.name)
                                        .filter(Boolean)
                                        .join(', ')
                                }}
                                sx={{ borderRadius: '8px', fontSize: '14px' }}
                            >
                                <MenuItem value="all">
                                    <Checkbox checked={targetType === 'all'} size="small" />
                                    <ListItemText primary="All Departments" />
                                </MenuItem>
                                {deptLoading ? (
                                    <MenuItem disabled>
                                        <Typography variant="body2" color="text.secondary">Loading...</Typography>
                                    </MenuItem>
                                ) : deptList.length === 0 ? (
                                    <MenuItem disabled>
                                        <Typography variant="body2" color="text.secondary">No departments found</Typography>
                                    </MenuItem>
                                ) : (
                                    deptList.map((dept) => (
                                        <MenuItem key={dept.id} value={String(dept.id)}>
                                            <Checkbox
                                                checked={targetType === 'department' && selectedDeptIds.includes(String(dept.id))}
                                                size="small"
                                            />
                                            <ListItemText primary={dept.name} />
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>
                    </Box>

                    <Divider sx={{ my: 2.5, display: detailLoading ? 'none' : 'block' }} />

                    {/* Attachments Section */}
                    <Box sx={{ display: detailLoading ? 'none' : 'block' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="subtitle2" fontWeight={600}>
                                Attachments
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {attachments.length}/5
                            </Typography>
                        </Box>

                        {/* File Upload Dropzone */}
                        <Box
                            {...getRootProps()}
                            sx={{
                                border: '2px dashed',
                                borderColor: isDragActive ? '#2666BE' : '#E0E0E0',
                                borderRadius: 2,
                                p: 4,
                                textAlign: 'center',
                                backgroundColor: isDragActive ? '#F0F7FF' : '#FAFAFA',
                                cursor: attachments.length >= 5 ? 'not-allowed' : 'pointer',
                                opacity: attachments.length >= 5 ? 0.6 : 1,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: attachments.length >= 5 ? '#FAFAFA' : '#F5F5F5',
                                    borderColor: attachments.length >= 5 ? '#E0E0E0' : '#2666BE',
                                },
                            }}
                        >
                            <input {...getInputProps()} />
                            <CloudUploadIcon sx={{ fontSize: 48, color: '#9E9E9E', mb: 1 }} />
                            <Typography variant="body1" fontWeight={500} color="text.primary" mb={0.5}>
                                {isDragActive ? 'Drop files here' : 'Drop files here'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                or click to upload (up to 5 files)
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                                Supported: Images, PDF, Word, Excel (limit to 5MB per file)
                            </Typography>
                        </Box>

                        {/* Uploaded Files List */}
                        {attachments.length > 0 && (
                            <Box mt={2}>
                                {attachments.map((file) => (
                                    <Box
                                        key={file.id}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        p={1.5}
                                        mb={1}
                                        sx={{
                                            border: '1px solid #E0E0E0',
                                            borderRadius: 1.5,
                                            backgroundColor: '#FAFAFA',
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" flex={1} minWidth={0}>
                                            <AttachFileIcon
                                                sx={{ fontSize: 20, color: 'text.secondary', mr: 1, flexShrink: 0 }}
                                            />
                                            <Box minWidth={0} flex={1}>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={500}
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {file.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {file.sizeFormatted}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box display="flex" gap={1} ml={2} flexShrink={0}>
                                            <Button
                                                size="small"
                                                onClick={() => handleViewFile(file)}
                                                sx={{
                                                    textTransform: 'none',
                                                    minWidth: 'auto',
                                                    px: 2,
                                                    fontSize: '0.875rem',
                                                    color: '#2666BE',
                                                }}
                                            >
                                                View
                                            </Button>
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    if (file._existing && file.id) {
                                                        setRemovedAttachmentIds((prev) => {
                                                            if (prev.includes(file.id)) return prev
                                                            return [...prev, file.id]
                                                        })
                                                    }
                                                    handleDeleteFile(file.id)
                                                }}
                                                sx={{
                                                    textTransform: 'none',
                                                    minWidth: 'auto',
                                                    px: 2,
                                                    fontSize: '0.875rem',
                                                    color: '#D32F2F',
                                                }}
                                            >
                                                Delete
                                            </Button>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2, gap: 1, backgroundColor: '#FBF5E2' }}>
                    <Button
                        onClick={handleClose}
                        variant="outlined"
                        sx={{
                            textTransform: 'none',
                            borderColor: '#F3BF3A',
                            color: 'text.primary',
                            fontSize: '14px',
                            fontWeight: 600,
                            px: '24px',
                            py: '10px',
                            minWidth: '150px',
                            borderRadius: '100px',
                            '&:hover': { borderColor: '#F3BF3A', backgroundColor: '#FDF6E2' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isDisabled}
                        variant="contained"
                        sx={{
                            textTransform: 'none',
                            backgroundColor: '#F3BF3A',
                            color: '#1A1A1A',
                            px: '24px',
                            py: '10px',
                            fontSize: '14px',
                            fontWeight: 600,
                            minWidth: '150px',
                            boxShadow: 'none',
                            borderRadius: '100px',
                            '&:hover': { backgroundColor: '#D09A01', boxShadow: 'none' },
                            '&.Mui-disabled': { backgroundColor: '#F3BF3A', opacity: 0.5 },
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Popover
                open={linkPopover.open}
                anchorEl={linkPopover.anchorEl}
                onClose={handleLinkClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                disableRestoreFocus
                PaperProps={{
                    sx: {
                        borderRadius: '10px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                        mt: '4px',
                    },
                }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    sx={{ px: 1.5, py: 1, gap: 1, width: 300 }}
                >
                    <InsertLink sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} />
                    <TextField
                        size="small"
                        placeholder="Paste a link..."
                        value={linkPopover.url}
                        onChange={(e) =>
                            setLinkPopover((prev) => ({ ...prev, url: e.target.value }))
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleLinkConfirm()
                            if (e.key === 'Escape') handleLinkClose()
                        }}
                        autoFocus
                        fullWidth
                        variant="standard"
                        InputProps={{ disableUnderline: true, sx: { fontSize: '14px' } }}
                    />
                    <IconButton
                        size="small"
                        onClick={handleLinkConfirm}
                        disabled={!linkPopover.url.trim()}
                        sx={{
                            color: linkPopover.url.trim() ? 'primary.main' : 'text.disabled',
                            flexShrink: 0,
                        }}
                    >
                        <CheckIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </Popover>
        </>
    )
}

export default EditAnnouncementModal
